import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Button from '../components/Button';
import AthleteLookupBox from '../components/camp/AthleteLookupBox';
import { STAT_OPTIONS } from '../lib/campStats';
import AdminPage, { AdminWindowSwitcher } from '../components/AdminShell';
import CampDataRoster from '../components/camp/CampDataRoster';
import './CampDataPage.css';

export default function CampDataPage() {
  const { API_URL } = useAuth();
  const [statName, setStatName] = useState('');
  const [query, setQuery] = useState('');
  const [value, setValue] = useState('');
  const [athlete, setAthlete] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [recentEntries, setRecentEntries] = useState([]);
  const [camps, setCamps] = useState([]);
  const [selectedCampId, setSelectedCampId] = useState('');
  const [dualVerify, setDualVerify] = useState(false);
  const [dualState, setDualState] = useState({ phase: 'idle' });
  // Roster (everyone at the camp, several measurables per athlete) or Station (one stat, scan and go).
  // Station stays the default because it is what runs at a live camp; the choice is remembered per browser.
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('pz.campdata.mode') === 'roster' ? 'roster' : 'station'; } catch { return 'station'; }
  });
  const changeMode = (m) => {
    setMode(m);
    try { localStorage.setItem('pz.campdata.mode', m); } catch { /* storage unavailable: the choice just won't persist */ }
  };
  const [showHandModal, setShowHandModal] = useState(false);
  const [handCapturing, setHandCapturing] = useState(false);
  const [handError, setHandError] = useState('');
  const [handPreview, setHandPreview] = useState(null);
  const searchRef = useRef(null);
  const valueRef = useRef(null);
  const pollRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const mobileFileRef = useRef(null);

  // Mobile detection
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Barcode scanner state & refs (separate from hand measurement webcam)
  const [scannerSupported, setScannerSupported] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const [lastScannedUid, setLastScannedUid] = useState('');
  const [scanFlash, setScanFlash] = useState(false);
  const scanVideoRef = useRef(null);
  const scanStreamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const detectorRef = useRef(null);

  const getStatLabel = (statValue) => {
    for (const group of STAT_OPTIONS) {
      const found = group.stats.find(s => s.value === statValue);
      if (found) return found.label;
    }
    return statValue;
  };

  // Fetch camps on mount
  useEffect(() => {
    const fetchCamps = async () => {
      try {
        const res = await axios.get(`${API_URL}/camps?include_past=true`);
        const allCamps = res.data || [];
        setCamps(allCamps);

        // Auto-select a camp happening today
        const today = new Date().toISOString().slice(0, 10);
        const todayCamp = allCamps.find(c => {
          const start = c.date?.slice(0, 10);
          const end = c.end_date?.slice(0, 10);
          if (start === today) return true;
          if (end && start <= today && end >= today) return true;
          return false;
        });
        if (todayCamp) setSelectedCampId(String(todayCamp.id));
      } catch (e) {
        console.error('Error fetching camps:', e);
      }
    };
    fetchCamps();
  }, [API_URL]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ── Barcode Scanner ──
  // Feature detection on mount
  useEffect(() => {
    if ('BarcodeDetector' in window) {
      try {
        detectorRef.current = new window.BarcodeDetector({ formats: ['code_128', 'data_matrix'] });
        setScannerSupported(true);
      } catch {
        setScannerSupported(false);
      }
    }
  }, []);

  // Play a short beep on successful scan using Web Audio API
  const playScanBeep = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 1200;
      gain.gain.value = 0.3;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.stop(ctx.currentTime + 0.15);
    } catch {
      // Audio not available, ignore
    }
  }, []);

  const startScanner = useCallback(async () => {
    if (scanStreamRef.current) return; // already running
    setScannerError('');
    try {
      const constraints = isMobile
        ? { video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } } }
        : { video: { width: { ideal: 640 }, height: { ideal: 480 } } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      scanStreamRef.current = stream;
      if (scanVideoRef.current) {
        scanVideoRef.current.srcObject = stream;
        scanVideoRef.current.play().catch(() => {});
      }
      setScannerActive(true);
    } catch (err) {
      console.error('Scanner camera error:', err.name, err.message);
      if (err.name === 'NotAllowedError') {
        setScannerError('Camera permission denied — check browser settings');
      } else if (err.name === 'NotFoundError') {
        setScannerError('No camera found on this device');
      } else if (err.name === 'NotReadableError') {
        setScannerError('Camera is in use by another app');
      } else {
        setScannerError(`Camera error: ${err.message}`);
      }
      setScannerActive(false);
    }
  }, [isMobile]);

  const stopScanner = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (scanStreamRef.current) {
      scanStreamRef.current.getTracks().forEach(t => t.stop());
      scanStreamRef.current = null;
    }
    setScannerActive(false);
  }, []);

  // Stop scanner when stat changes away from hand_size
  useEffect(() => {
    if (statName !== 'hand_size') {
      stopScanner();
    }
    return () => stopScanner();
  }, [statName, stopScanner]);

  // Attach stream to video element when it mounts (fixes ref-not-ready race)
  useEffect(() => {
    if (scanVideoRef.current && scanStreamRef.current && !scanVideoRef.current.srcObject) {
      scanVideoRef.current.srcObject = scanStreamRef.current;
      scanVideoRef.current.play().catch(() => {});
    }
  }, [scannerActive]);

  const selectAthlete = (a) => {
    setAthlete(a);
    setQuery(`${a.first_name} ${a.last_name}`);
    setLookupError('');
    setTimeout(() => valueRef.current?.focus(), 50);
  };

  const lookupByUid = useCallback(async (uid) => {
    try {
      const res = await axios.get(`${API_URL}/athletes/lookup/${encodeURIComponent(uid.trim())}`);
      if (res.data?.blank_walkin_band || !res.data?.user_id) {
        setLookupError('No athlete found for that UID');
        return;
      }
      selectAthlete(res.data);
    } catch {
      setLookupError('No athlete found for that UID');
    }
  }, [API_URL]);

  // Scanning loop — pauses when athlete is selected
  useEffect(() => {
    if (!scannerActive || !detectorRef.current || !scanVideoRef.current) {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      return;
    }

    // Clear previous interval
    if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);

    scanIntervalRef.current = setInterval(async () => {
      // Pause detection when athlete is already selected
      if (athlete) return;
      const video = scanVideoRef.current;
      if (!video || video.readyState < 2) return;
      try {
        const barcodes = await detectorRef.current.detect(video);
        for (const barcode of barcodes) {
          const raw = barcode.rawValue;
          if (/^\d{8}$/.test(raw) && raw !== lastScannedUid) {
            setLastScannedUid(raw);
            setScanFlash(true);
            playScanBeep();
            setTimeout(() => setScanFlash(false), 400);
            lookupByUid(raw);
            break;
          }
        }
      } catch {
        // detection error, ignore
      }
    }, 200);

    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
    };
  }, [scannerActive, athlete, lastScannedUid, lookupByUid, playScanBeep]);

  // Typing in the Athlete box (AthleteLookupBox owns the search and the dropdown).
  const handleQueryChange = (text) => {
    setQuery(text);
    setAthlete(null);
    setLookupError('');
  };

  const resetForNextEntry = () => {
    setQuery('');
    setValue('');
    setAthlete(null);
    setLookupError('');
    setLastScannedUid('');
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const addRecentEntry = (val) => {
    const campObj = camps.find(c => String(c.id) === selectedCampId);
    const entry = {
      name: `${athlete.first_name} ${athlete.last_name}`,
      stat: getStatLabel(statName),
      value: val,
      camp: campObj?.name || '',
      time: new Date().toLocaleTimeString(),
    };
    setRecentEntries(prev => [entry, ...prev].slice(0, 20));
  };

  const startPolling = (entryId, athleteRef) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/athletes/${athleteRef.user_id}/dual-entry-status`, {
          params: { entry_id: entryId },
        });
        if (res.data.status === 'matched') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setDualState({ phase: 'matched', value: res.data.value });
          addRecentEntry(res.data.value);
          setToast(`MATCH — ${getStatLabel(statName)} = ${res.data.value} saved & verified`);
          setTimeout(() => { setToast(''); setDualState({ phase: 'idle' }); resetForNextEntry(); }, 3000);
        } else if (res.data.status === 'mismatched') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setDualState({
            phase: 'mismatched',
            entry1_value: res.data.entry1_value,
            entry2_value: res.data.entry2_value,
          });
        } else if (res.data.status === 'expired') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setDualState({ phase: 'idle' });
          setToast('Entry expired (>10 min). Please retry.');
          setTimeout(() => setToast(''), 4000);
        }
      } catch {
        // ignore polling errors
      }
    }, 2500);
  };

  const handleDualClear = async () => {
    if (!athlete) return;
    try {
      await axios.post(`${API_URL}/athletes/${athlete.user_id}/dual-entry-clear`, {
        stat_name: statName,
        camp_id: selectedCampId,
      });
    } catch { /* ignore */ }
    setDualState({ phase: 'idle' });
    setValue('');
  };

  const handleConfirm = async () => {
    if (!athlete || !statName || !value) return;

    // Dual-verify mode
    if (dualVerify) {
      if (!selectedCampId) {
        setToast('Error: Select a camp for dual-verify mode');
        setTimeout(() => setToast(''), 3000);
        return;
      }
      setSaving(true);
      try {
        const res = await axios.post(`${API_URL}/athletes/${athlete.user_id}/dual-entry-submit`, {
          stat_name: statName,
          value,
          camp_id: selectedCampId,
        });
        if (res.data.result === 'waiting') {
          setDualState({ phase: 'waiting', entryId: res.data.entry_id });
          startPolling(res.data.entry_id, athlete);
        } else if (res.data.result === 'matched') {
          setDualState({ phase: 'matched', value: res.data.value });
          addRecentEntry(res.data.value);
          setToast(`MATCH — ${getStatLabel(statName)} = ${res.data.value} saved & verified`);
          setTimeout(() => { setToast(''); setDualState({ phase: 'idle' }); resetForNextEntry(); }, 3000);
        } else if (res.data.result === 'mismatched') {
          setDualState({
            phase: 'mismatched',
            entry1_value: res.data.entry1_value,
            entry2_value: res.data.entry2_value,
          });
        }
      } catch (err) {
        setToast('Error: ' + (err.response?.data?.detail || err.message));
        setTimeout(() => setToast(''), 4000);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Single-entry mode (existing behavior)
    setSaving(true);
    try {
      await axios.post(`${API_URL}/athletes/${athlete.user_id}/update-stat`, {
        stat_name: statName,
        value: value,
      });
      await axios.post(`${API_URL}/athletes/${athlete.user_id}/verify`, {
        stat: statName,
        verified: true,
        camp_id: selectedCampId || undefined,
      });

      addRecentEntry(value);
      setToast(`Saved: ${athlete.first_name} ${athlete.last_name} — ${getStatLabel(statName)} = ${value}`);
      setTimeout(() => setToast(''), 3000);
      resetForNextEntry();
    } catch (err) {
      setToast('Error saving: ' + (err.response?.data?.detail || err.message));
      setTimeout(() => setToast(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleValueKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };

  // ── Hand Measurement helpers ──
  const startWebcam = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.log('getUserMedia not available (requires HTTPS)');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.log('Webcam not available:', err.message);
    }
  }, []);

  const openHandModal = () => {
    stopScanner();
    setHandPreview(null);
    setHandError('');
    setHandCapturing(false);
    setShowHandModal(true);
  };

  // Start webcam when modal opens (desktop only)
  useEffect(() => {
    if (showHandModal && !handPreview && !isMobile) {
      startWebcam();
    }
    return () => {
      // Cleanup stream when modal closes
      if (!showHandModal && streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [showHandModal, handPreview, startWebcam, isMobile]);

  const closeHandModal = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setShowHandModal(false);
    setHandPreview(null);
    setHandError('');
    setHandCapturing(false);
    if (scannerSupported) startScanner();
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setHandPreview(dataUrl);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setHandPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const measureHand = async () => {
    if (!handPreview) return;
    setHandCapturing(true);
    setHandError('');
    try {
      // Convert data URL to Blob
      const res = await fetch(handPreview);
      const blob = await res.blob();
      const formData = new FormData();
      formData.append('file', blob, 'hand.jpg');
      const resp = await axios.post(`${API_URL}/athletes/measure-hand`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = resp.data;
      setValue(String(result.hand_size_inches));
      closeHandModal();
      setToast(`Hand measured: ${result.hand_size_inches}" (${result.confidence} confidence, ${result.markers_detected} markers)`);
      setTimeout(() => setToast(''), 4000);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setHandError(detail);
    } finally {
      setHandCapturing(false);
    }
  };

  const printHandCard = async () => {
    try {
      const resp = await axios.get(`${API_URL}/athletes/hand-card`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(resp.data);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframe.src = blobUrl;
      iframe.onload = () => {
        try {
          iframe.contentWindow.print();
        } catch {
          // Cross-origin fallback: open in new tab
          window.open(blobUrl, '_blank');
        }
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 60000);
      };
    } catch (err) {
      setToast('Error: Could not load hand card PDF');
      setTimeout(() => setToast(''), 3000);
    }
  };

  const formatCampDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const campOptionsJsx = (
    <>
      {[
              { kind: 'camp', label: 'Camps' },
              { kind: 'clinic', label: 'Clinics' },
            ].map(group => {
              const items = camps.filter(c => (c.event_type || 'camp') === group.kind);
              if (items.length === 0) return null;
              return (
                <optgroup key={group.kind} label={group.label}>
                  {items.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name} — {formatCampDate(c.date)}
                    </option>
                  ))}
                </optgroup>
              );
            })}
    </>
  );

  // Roster mode has no camera of its own: hand the athlete to the station, set up for a hand-size measurement.
  const captureHandFor = (row) => {
    const [first, ...rest] = (row.name || '').split(' ');
    changeMode('station');
    setStatName('hand_size');
    selectAthlete({ user_id: row.user_id, first_name: first || '', last_name: rest.join(' '), profile_photo: null, uid: null });
  };

  const modeSwitch = (
    <AdminWindowSwitcher label="Mode" value={mode} onChange={changeMode}
      options={[{ value: 'roster', label: 'Roster' }, { value: 'station', label: 'Station' }]} />
  );

  if (mode === 'roster') {
    return (
      <AdminPage active="camps" title="Camp data" actions={modeSwitch}
        subtitle="Everyone registered for the camp: record each athlete's measurables, then Save & next."
        breadcrumb={[{ label: 'Camps', to: '/camps' }, { label: 'Camp data' }]}>
        <div className="camp-data camp-data--wide">
          <div className="cd-mode">
            <select className="adm-select cd-camp" aria-label="Camp / Clinic" value={selectedCampId} onChange={(e) => setSelectedCampId(e.target.value)}>
              <option value="">— Select a camp or clinic —</option>
              {campOptionsJsx}
            </select>
          </div>
          <CampDataRoster campId={selectedCampId} onCaptureHand={captureHandFor} />
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage active="camps" title="Camp data" actions={modeSwitch}
      subtitle="Record one stat at a time: pick the camp and the stat, then scan or search each athlete."
      breadcrumb={[{ label: 'Camps', to: '/camps' }, { label: 'Camp data' }]}>
    <div className="camp-data">

      {/* Camp + Stat Type — 2 columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div className="form-field">
          <label className="form-field__label">Camp / Clinic</label>
          <select
            className="form-field__select"
            value={selectedCampId}
            onChange={e => setSelectedCampId(e.target.value)}
          >
            <option value="">— Select a camp or clinic —</option>
            {campOptionsJsx}
          </select>
        </div>
        <div className="form-field">
          <label className="form-field__label">Stat Type</label>
          <select
            className="form-field__select"
            value={statName}
            onChange={e => setStatName(e.target.value)}
          >
            <option value="">— Select a stat —</option>
            {STAT_OPTIONS.map(group => (
              <optgroup key={group.group} label={group.group}>
                {group.stats.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>

      {/* Dual Verify toggle OR Print Card button */}
      {statName === 'hand_size' ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
          padding: '12px 16px', borderRadius: 8,
          background: '#f9fafb', border: '1px solid #e5e7eb',
        }}>
          <Button
            variant="secondary"
            onClick={printHandCard}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print Reference Card
          </Button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Print ProZpect hand measurement card
          </span>
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
          padding: '12px 16px', borderRadius: 8,
          background: dualVerify ? '#eff6ff' : '#f9fafb',
          border: `1px solid ${dualVerify ? '#93c5fd' : '#e5e7eb'}`,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
            <div
              onClick={() => { setDualVerify(v => !v); setDualState({ phase: 'idle' }); if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }}
              style={{
                width: 44, height: 24, borderRadius: 12, position: 'relative', cursor: 'pointer',
                background: dualVerify ? '#3b82f6' : '#d1d5db', transition: 'background 0.2s',
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 2, left: dualVerify ? 22 : 2,
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
              }} />
            </div>
            <span style={{ fontWeight: 600, fontSize: 14, color: dualVerify ? '#3F6B53' : '#6b7280' }}>
              Dual Verify
            </span>
          </label>
          {dualVerify && (
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Two stations must enter the same value to save & verify
            </span>
          )}
        </div>
      )}

      {/* Dual-entry status banners */}
      {dualVerify && dualState.phase === 'waiting' && (
        <div style={{
          padding: '16px 20px', borderRadius: 8, marginBottom: 20,
          background: '#fefce8', border: '2px solid #fde68a',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 12, height: 12, borderRadius: '50%', background: '#f59e0b',
            animation: 'pulse 1.5s ease-in-out infinite',
          }} />
          <span style={{ fontWeight: 600, fontSize: 15 }}>Waiting for second entry...</span>
          <Button
            variant="secondary"
            onClick={() => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } handleDualClear(); }}
            style={{ marginLeft: 'auto' }}
          >
            Cancel
          </Button>
        </div>
      )}
      {dualVerify && dualState.phase === 'matched' && (
        <div style={{
          padding: '16px 20px', borderRadius: 8, marginBottom: 20,
          background: '#f0fdf4', border: '2px solid #86efac',
          fontWeight: 700, fontSize: 16, color: '#15803d',
        }}>
          MATCH — {getStatLabel(statName)} = {dualState.value} saved & verified
        </div>
      )}
      {dualVerify && dualState.phase === 'mismatched' && (
        <div style={{
          padding: '16px 20px', borderRadius: 8, marginBottom: 20,
          background: '#fef2f2', border: '2px solid #fca5a5',
        }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#dc2626', marginBottom: 8 }}>
            MISMATCH — Re-measure required
          </div>
          <div style={{ fontSize: 14, color: '#7f1d1d', marginBottom: 12 }}>
            Entry 1: <strong>{dualState.entry1_value}</strong> &nbsp; Entry 2: <strong>{dualState.entry2_value}</strong>
          </div>
          <Button
            variant="danger"
            onClick={handleDualClear}
          >
            Clear & Retry
          </Button>
        </div>
      )}

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>

      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.startsWith('Error') ? '#ef4444' : '#16a34a',
          color: '#fff', padding: '12px 20px', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,.15)', fontWeight: 500,
          maxWidth: 400,
        }}>
          {toast}
        </div>
      )}

      {/* Athlete card — always visible, green box */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 24,
        padding: '24px 28px', borderRadius: 12, marginBottom: 20,
        background: '#f0fdf4',
        border: scanFlash ? '2px solid #22c55e' : '2px solid #bbf7d0',
        transition: 'border-color 0.2s',
        position: 'relative',
      }}>
        {athlete ? (
          <>
            {athlete.profile_photo ? (
              <img src={athlete.profile_photo} alt="" style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{
                width: 96, height: 96, borderRadius: '50%', background: '#d1d5db',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 32, fontWeight: 700, color: '#6b7280', flexShrink: 0,
              }}>
                {athlete.first_name?.[0]}{athlete.last_name?.[0]}
              </div>
            )}
            <div style={{ fontWeight: 700, fontSize: 32, lineHeight: 1.2 }}>
              {athlete.first_name} {athlete.last_name}
            </div>
          </>
        ) : scannerActive ? (
          <>
            {/* Placeholder for scanner video (actual video is absolutely positioned) */}
            <div style={{
              width: 160, height: 120, borderRadius: 8, flexShrink: 0,
            }} />
            <div>
              <div style={{ color: '#16a34a', fontSize: 20, fontWeight: 600 }}>
                Scan Badge Barcode
              </div>
              <div style={{ color: '#86efac', fontSize: 13, marginTop: 4 }}>
                Hold wristband in front of camera
              </div>
              {isMobile && (
                <Button
                  variant="danger"
                  onClick={stopScanner}
                  style={{ marginTop: 8 }}
                >
                  Stop Camera
                </Button>
              )}
              {scannerError && (
                <div style={{ color: '#ef4444', fontSize: 13, marginTop: 4 }}>
                  {scannerError} — use manual entry below
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{
              width: 96, height: 96, borderRadius: '50%', background: '#dcfce7',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#86efac" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div>
              <div style={{ color: '#86efac', fontSize: 24, fontWeight: 600 }}>
                Scan Athlete ID
              </div>
              {scannerSupported && statName === 'hand_size' && (
                <Button
                  variant="accent"
                  onClick={startScanner}
                  style={{ marginTop: 8 }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Scan Badge
                </Button>
              )}
              {scannerError && (
                <div style={{ color: '#ef4444', fontSize: 13, marginTop: 4 }}>
                  {scannerError}
                </div>
              )}
            </div>
          </>
        )}
        {/* Scanner video element — mounted when active */}
        <video
          ref={scanVideoRef}
          autoPlay playsInline muted
          style={{
            position: 'absolute',
            borderRadius: athlete ? 6 : 8,
            objectFit: 'cover',
            transition: 'all 0.3s ease',
            display: scannerActive ? 'block' : 'none',
            ...(athlete
              ? { top: 8, right: 8, width: 80, height: 60, opacity: 0.5 }
              : {
                  top: 24, left: 28, width: 160, height: 120, opacity: 1,
                  border: scanFlash ? '2px solid #22c55e' : '2px solid #dcfce7',
                }
            ),
          }}
        />
      </div>

      {/* Entry Form */}
      <div className="settings-section">
        <h2 className="settings-section__title">Entry Form</h2>

        {/* Athlete search: name typeahead + barcode scans (Enter on typed text = scan) */}
        <div className="form-field" style={{ marginBottom: 16, position: 'relative' }}>
          <label className="form-field__label">Athlete</label>
          <AthleteLookupBox
            apiUrl={API_URL}
            value={query}
            onValueChange={handleQueryChange}
            onSelect={selectAthlete}
            onScan={lookupByUid}
            athleteSelected={!!athlete}
            inputRef={searchRef}
          />
          {lookupError && !athlete && <p style={{ color: '#ef4444', fontSize: 13, marginTop: 4 }}>{lookupError}</p>}
        </div>

        {/* Value input — with optional hand capture button */}
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label">Value</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <input
              ref={valueRef}
              className="form-field__input"
              type="number"
              step="any"
              placeholder={statName ? `Enter ${getStatLabel(statName)}` : 'Select a stat first'}
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleValueKeyDown}
              disabled={!athlete || (dualVerify && dualState.phase !== 'idle')}
              style={{ flex: 1, width: 'auto', minWidth: 0 }}
            />
            {statName === 'hand_size' && (
              <Button
                variant="secondary"
                onClick={() => openHandModal()}
                disabled={!athlete}
                style={{ flexShrink: 0, position: 'relative', zIndex: 2 }}
                title="Capture hand photo for auto-measurement"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                Capture Hand
              </Button>
            )}
          </div>
        </div>

        {/* Confirm button */}
        <Button
          variant="danger"
          fullWidth
          onClick={handleConfirm}
          disabled={!athlete || !statName || !value || saving || (dualVerify && dualState.phase !== 'idle')}
          style={{ fontSize: 16 }}
        >
          {saving ? 'Saving...' : 'Confirm & Save'}
        </Button>
      </div>

      {/* Recent entries */}
      {recentEntries.length > 0 && (
        <div className="settings-section">
          <h2 className="settings-section__title">Recent Entries ({recentEntries.length})</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '8px 12px' }}>Time</th>
                  <th style={{ padding: '8px 12px' }}>Athlete</th>
                  <th style={{ padding: '8px 12px' }}>Stat</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {recentEntries.map((entry, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>{entry.time}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{entry.name}</td>
                    <td style={{ padding: '8px 12px' }}>{entry.stat}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{entry.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Hand Capture Modal */}
      {showHandModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={(e) => { if (e.target === e.currentTarget) closeHandModal(); }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24, maxWidth: 640, width: '95%',
            maxHeight: '90vh', overflowY: 'auto', position: 'relative',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Hand Size Measurement</h3>
              <Button variant="ghost" iconOnly onClick={closeHandModal} style={{ fontSize: 22 }}>&times;</Button>
            </div>

            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Place the athlete's hand flat on the printed reference card with fingers spread.
              The thumb and pinky should be extended as far as possible (NFL Combine style).
            </p>

            {/* Webcam feed or preview */}
            {!handPreview ? (
              <div>
                {isMobile ? (
                  /* Mobile: native camera via label tap (direct user gesture) */
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    padding: '40px 20px', background: '#f9fafb', borderRadius: 8,
                  }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4D8064" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <label style={{
                      padding: '14px 32px', borderRadius: 8, border: 'none',
                      background: '#4D8064', color: '#fff', fontWeight: 600, fontSize: 16,
                      cursor: 'pointer', marginBottom: 12, textAlign: 'center',
                    }}>
                      Take Photo
                      <input
                        ref={mobileFileRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <label style={{
                      padding: '10px 24px', borderRadius: 8, border: '1px solid #d1d5db',
                      background: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                      color: '#374151', textAlign: 'center',
                    }}>
                      Choose from Gallery
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                ) : (
                  /* Desktop: webcam stream */
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        width: '100%', borderRadius: 8, background: '#000',
                        maxHeight: 360, objectFit: 'contain',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <Button
                        variant="primary"
                        onClick={capturePhoto}
                        style={{ flex: 1 }}
                      >
                        Capture Photo
                      </Button>
                      <label style={{
                        flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #d1d5db',
                        background: '#f9fafb', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                        textAlign: 'center', color: '#374151',
                      }}>
                        Upload File
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div>
                <img
                  src={handPreview}
                  alt="Hand preview"
                  style={{
                    width: '100%', borderRadius: 8, maxHeight: 360, objectFit: 'contain',
                    background: '#f3f4f6',
                  }}
                />
                {handError && (
                  <div style={{
                    marginTop: 12, padding: '10px 14px', borderRadius: 8,
                    background: '#fef2f2', border: '1px solid #fca5a5',
                    color: '#dc2626', fontSize: 13,
                  }}>
                    {handError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <Button
                    variant="secondary"
                    onClick={() => { setHandPreview(null); setHandError(''); }}
                    disabled={handCapturing}
                    style={{ flex: 1 }}
                  >
                    Retake
                  </Button>
                  <Button
                    variant="primary"
                    onClick={measureHand}
                    disabled={handCapturing}
                    style={{ flex: 1 }}
                  >
                    {handCapturing ? 'Measuring...' : 'Measure Hand'}
                  </Button>
                </div>
              </div>
            )}

            {/* Download reference card link */}
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <a
                href={`${API_URL}/athletes/hand-card`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12, color: '#6b7280' }}
              >
                Download printable reference card (PDF)
              </a>
            </div>

            {/* Hidden canvas for capture */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        </div>
      )}
    </div>
    </AdminPage>
  );
}
